import React, { FC, useMemo } from 'react';
import { getCurrency, nullSafeGet } from '../../../utils/DataAccessUtils';
import WorkOrderDisplayStatusDisplay from '../../work_order_display_status_display/WorkOrderDisplayStatusDisplay';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import QuoteDisplayStatusDisplay from '../../quote_display_status_display/QuoteDisplayStatusDisplay';
import { QUOTE_STATUS_DISPLAY_NAMES } from '../../../constants/QuoteStatuses';
import InvoiceDisplayStatusDisplay from '../../invoice_display_status_display/InvoiceDisplayStatusDisplay';
import { INVOICE_STATUS_DISPLAY_NAMES } from '../../../constants/InvoiceStatuses';
import { withUserType } from '../../withUserType';
import { connect } from 'react-redux';
import {
	CONTRACTOR_WORK_ORDER_NOTES,
	CONTRACTOR_WORK_ORDER_NOTES_MENTIONS,
} from '../../../utils/NotificationUtils';

const CONTRACTOR_NOTES_NOTIFICATION_TYPES = [
	CONTRACTOR_WORK_ORDER_NOTES,
	CONTRACTOR_WORK_ORDER_NOTES_MENTIONS,
];

const NoteTextDisplay: FC<any> = ({ noteText }) =>
	noteText ? (
		<div className="mt-4 rounded-xl bg-slate-200 p-3 text-base text-gray-500 ">"{noteText}"</div>
	) : null;

const NotificationTemplate: FC<any> = ({ notification, userType }): React.ReactElement => {
	const WODetails = useMemo(
		() => (
			<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
				<span>WO #{nullSafeGet('workOrder.id', notification)}</span>
				{/* <WOSubscribeAction notification={notification} onRefresh={onRefresh} /> */}
			</div>
		),
		[notification]
	);

	// The API nests the type under `{userType}PushNotification`, so the switch below —
	// which reads it off the top level — never matches a contractor note notification.
	// Those are resolved here so notes on subcontracted work orders show their text.
	// Contractor note types are supplier-only. (OPE-5106)
	const contractorNotesNotificationType = nullSafeGet(
		`${userType}PushNotification.notificationType`,
		notification
	);
	if (CONTRACTOR_NOTES_NOTIFICATION_TYPES.includes(contractorNotesNotificationType)) {
		return (
			<NoteTextDisplay
				noteText={nullSafeGet(`${userType}PushNotification.noteText`, notification)}
			/>
		);
	}

	switch (notification.notificationType) {
		case 'WorkOrderNotes':
		case 'WorkOrderNotesMentions':
			const noteText =
				ROLE_TYPES.SUPPLIER === userType
					? nullSafeGet('supplierPushNotification.noteText', notification)
					: nullSafeGet('buyerPushNotification.noteText', notification);
			return <NoteTextDisplay noteText={noteText} />;

		case 'InvoiceApproval':
		case 'InvoiceApprovalReminder':
		case 'Invoiced':
			return nullSafeGet('invoice.id', notification) &&
				nullSafeGet('workOrder.id', notification) ? (
				<div className="mt-2 grid gap-x-4 gap-y-3 rounded-xl border-2 border-solid border-slate-200 p-3 text-base text-gray-500">
					<div className="font-semibold">{nullSafeGet('workOrder.title', notification)}</div>
					<div className="flex flex-row  items-center gap-x-4">
						<div className="rowInlineGroup">
							<span className="rowGrayChip">
								<i className="icons8-font icons8-location-marker text-md" />
								<span>{nullSafeGet('workOrder.location.name', notification)}</span>
							</span>
						</div>
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<InvoiceDisplayStatusDisplay
								status={INVOICE_STATUS_DISPLAY_NAMES[nullSafeGet('invoice.status', notification)]}
							/>
						</div>
						{getCurrency({ workOrder: nullSafeGet('workOrder', notification) }).format(
							nullSafeGet('invoice.invoiceTotalAfterTax', notification)
						)}
						{WODetails}
					</div>
				</div>
			) : null;

		case 'ProposalApproval':
		case 'ProposalApprovalReminder':
		case 'RFP_RequestProposal':
		case 'RFP_Participating':
		case 'RFP_NotParticipating':
		case 'RFP_Awarded':
		case 'RFP_Declined':
		case 'RFP_RequestForProposalEnded':
		case 'WaitingForQuote':
		case 'Proposal':
		case 'ProposalAwarded':
		case 'ProposalDeclined':
			return nullSafeGet('quote.id', notification) && nullSafeGet('workOrder.id', notification) ? (
				<div className="mt-2 grid gap-x-4 gap-y-3 rounded-xl border-2 border-solid border-slate-200 p-3 text-base text-gray-500">
					<div className="font-semibold">{nullSafeGet('workOrder.title', notification)}</div>
					<div className="flex flex-row  items-center gap-x-4">
						<div className="rowInlineGroup">
							<span className="rowGrayChip">
								<i className="icons8-font icons8-location-marker text-md" />
								<span>{nullSafeGet('workOrder.location.name', notification)}</span>
							</span>
						</div>
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<QuoteDisplayStatusDisplay
								status={QUOTE_STATUS_DISPLAY_NAMES[nullSafeGet('quote.status', notification)]}
							/>
						</div>
						{getCurrency({ workOrder: nullSafeGet('workOrder', notification) }).format(
							nullSafeGet('quote.totalAfterTax', notification)
						)}
						{WODetails}
					</div>
				</div>
			) : null;

		default:
			return nullSafeGet('workOrder.id', notification) ? (
				<div className="mt-2 grid gap-x-4 gap-y-3 rounded-xl border-2 border-solid border-slate-200 p-3 text-base text-gray-500">
					<div className="font-semibold">{nullSafeGet('workOrder.title', notification)}</div>
					<div className="flex flex-row  items-center gap-x-4">
						<div className="rowInlineGroup">
							<span className="rowGrayChip">
								<i className="icons8-font icons8-location-marker text-md" />
								<span>{nullSafeGet('workOrder.location.name', notification)}</span>
							</span>
						</div>
						<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
							<WorkOrderDisplayStatusDisplay
								status={nullSafeGet('workOrder.displayStatus', notification)}
							/>
						</div>
						{WODetails}
					</div>
				</div>
			) : null;
	}
};

export default withUserType(connect()(NotificationTemplate));
